import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Avatar,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  Pagination,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  alpha,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import UserPayinGatewaysCard from "../components/UserPayinGatewaysCard";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import ReceiptRoundedIcon from "@mui/icons-material/ReceiptRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {
  adminGetUserDetail,
  adminGetUserCommissions,
  lockUser,
  unlockUser,
  adjustUserWallet,
  adminUpdateUser,
  adminSetUserActive,
  adminSetApproval,
  adminUploadKyc,
  adminGetMovePreview,
  adminMoveUser,
  adminListPosMachines,
  adminGetUserPosMachines,
  adminSetUserPosMachines,
} from "../api";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

/* Where a commission row came from — lets admin spot test/manual rows that
   have no backing payin. */
const COMMISSION_SOURCE_LABEL = {
  payin: "Payin",
  bbps: "Bill",
  manual: "Manual",
};

const ROLE_COLORS = {
  ADMIN: "#1c6b8c",
  ADMIN_PARTNER: "#2f8fb5",
  WHITE_LABEL: "#a855f7",
  STATE_HEAD: "#0ea5e9",
  SUPER_DISTRIBUTOR: "#06b6d4",
  MASTER_DISTRIBUTOR: "#14b8a6",
  DISTRIBUTOR: "#22c55e",
  RETAILER: "#f59e0b",
  SHOPKEEPER: "#f97316",
};

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

function StatCard({
  icon,
  label,
  value,
  sub,
  color = "primary.main",
  bgcolor,
}) {
  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: bgcolor || "background.paper",
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Avatar sx={{ bgcolor: alpha(color, 0.14), color, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

function MonthlyChart({ monthlyPayins, monthlyPayouts, monthlyCommissions }) {
  const allMonths = new Set();
  [monthlyPayins, monthlyPayouts, monthlyCommissions].forEach((arr) =>
    arr.forEach((r) => allMonths.add(r.month)),
  );
  const months = [...allMonths].sort();
  if (months.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="text.secondary">
          No data in the last 6 months
        </Typography>
      </Box>
    );
  }
  const chartData = months.map((m) => ({
    month: m,
    Payins: Number(monthlyPayins.find((r) => r.month === m)?.total || 0),
    Payouts: Number(monthlyPayouts.find((r) => r.month === m)?.total || 0),
    Commissions: Number(
      monthlyCommissions.find((r) => r.month === m)?.total || 0,
    ),
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData}
        margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis
          tick={{ fontSize: 12 }}
          tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
        />
        <RTooltip formatter={(v) => INR(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="Payins" fill="#22c55e" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Payouts" fill="#ef4444" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Commissions" fill="#1c6b8c" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

const KYC_DOC_FIELDS = [
  ["PAN", "pan"],
  ["Aadhaar", "aadhaar"],
  ["Cancelled cheque", "cheque"],
];

const isImageUrl = (url) => {
  if (!url) return false;
  const path = url.split("?")[0].toLowerCase();
  return /\.(jpe?g|png|webp)$/.test(path);
};

const approvalColor = (s) => {
  if (s === "approved") return "success";
  if (s === "rejected") return "error";
  if (s === "pending") return "warning";
  return "default";
};

function KycDocItem({ label, url, docKey, onUpload }) {
  const [imgError, setImgError] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const showImage = isImageUrl(url) && !imgError;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onUpload) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert("File must be 5 MB or smaller");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await onUpload(docKey, reader.result);
      } finally {
        setBusy(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      {!url ? (
        <Button size="small" variant="outlined" disabled>
          Not provided
        </Button>
      ) : showImage ? (
        <Box
          component="img"
          src={url}
          alt={label}
          onError={() => setImgError(true)}
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          sx={{
            maxHeight: 120,
            maxWidth: "100%",
            objectFit: "contain",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
            display: "block",
          }}
        />
      ) : (
        <Button
          size="small"
          variant="outlined"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {isImageUrl(url) ? "View document" : "View PDF"}
        </Button>
      )}
      {onUpload && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={handleFile}
          />
          <Button
            size="small"
            variant="text"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            sx={{ mt: 0.5 }}
          >
            {busy ? "Uploading…" : url ? "Replace" : "Upload"}
          </Button>
        </>
      )}
    </Box>
  );
}

function KycDocumentsSection({ user, kycDocuments, onUpload }) {
  const docs = kycDocuments || {};
  const status = user.approval_status;
  return (
    <Card
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>
          KYC Documents
        </Typography>
        {status && (
          <Chip
            size="small"
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            color={approvalColor(status)}
            sx={{ fontWeight: 600, fontSize: 11 }}
          />
        )}
      </Stack>
      {status === "rejected" && user.rejection_reason && (
        <Typography variant="body2" color="error" mb={2}>
          Reason: {user.rejection_reason}
        </Typography>
      )}
      <Grid container spacing={2}>
        {KYC_DOC_FIELDS.map(([label, key]) => (
          <Grid item xs={12} sm={4} key={key}>
            <KycDocItem
              label={label}
              url={docs[key]}
              docKey={key}
              onUpload={onUpload}
            />
          </Grid>
        ))}
      </Grid>
    </Card>
  );
}


function ProfileSection({ user }) {
  const roleName = user.role_name || "—";
  const copyId = () => navigator.clipboard?.writeText(user.id);
  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-start">
        <Avatar
          sx={{
            width: 72,
            height: 72,
            bgcolor: ROLE_COLORS[roleName] || "#1c6b8c",
            fontSize: 26,
            fontWeight: 700,
          }}
        >
          {(user.first_name || "?")[0]}
          {(user.last_name || "?")[0]}
        </Avatar>
        <Box flex={1} minWidth={200}>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="h5" fontWeight={700}>
              {user.first_name} {user.last_name}
            </Typography>
            <Chip
              size="small"
              label={(roleName || "").replace(/_/g, " ")}
              sx={{
                bgcolor: `${ROLE_COLORS[roleName] || "#1c6b8c"}15`,
                color: ROLE_COLORS[roleName] || "#1c6b8c",
                fontWeight: 600,
                fontSize: 11,
              }}
            />
            <Chip
              size="small"
              label={user.is_active ? "Active" : "Inactive"}
              color={user.is_active ? "success" : "default"}
              sx={{ fontWeight: 600, fontSize: 11 }}
            />
            {user.is_locked && (
              <Tooltip title={user.locked_reason || ""} arrow>
                <Chip
                  size="small"
                  icon={<LockRoundedIcon sx={{ fontSize: 14 }} />}
                  label="Wallet Locked"
                  color="error"
                  sx={{ fontWeight: 600, fontSize: 11 }}
                />
              </Tooltip>
            )}
          </Stack>
          {user.is_locked && user.locked_reason && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              mb={0.5}
            >
              Locked: {user.locked_reason}
            </Typography>
          )}
          <Stack direction="row" gap={0.5} alignItems="center" mb={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace", fontSize: 11 }}
            >
              ID: {user.id}
            </Typography>
            <IconButton size="small" onClick={copyId} sx={{ p: 0.25 }}>
              <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
          <Grid container spacing={2}>
            {[
              ["Email", user.email],
              ["Phone", user.mobile_number],
              ["City", user.city],
              ["State", user.state],
              ["PAN", user.pan_card_number],
              [
                "Aadhaar",
                user.aadhar_card_number
                  ? `•••• ${user.aadhar_card_number.slice(-4)}`
                  : null,
              ],
              [
                "Bank A/C",
                user.bank_account_number
                  ? `•••• ${user.bank_account_number.slice(-4)}`
                  : null,
              ],
              ["IFSC", user.ifsc_code],
              ["Parent", user.parent_name],
              ["Joined", fmtDate(user.created_at)],
              ["Deposit Limit", INR(user.deposit_limit)],
              ["Withdraw Limit", INR(user.withdraw_daily_limit)],
            ].map(([label, val]) => (
              <Grid item xs={6} sm={4} md={3} key={label}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {val || "—"}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Card>
  );
}

function TransactionsTab({ transactions }) {
  if (!transactions?.length) return <EmptyState label="No transactions" />;
  return (
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>Type</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Wallet</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Ref</TableCell>
          <TableCell>Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {transactions.map((t) => (
          <TableRow key={t.id} hover>
            <TableCell>
              <Chip
                size="small"
                label={(t.type || "").toUpperCase()}
                color={t.type === "credit" ? "success" : "error"}
                sx={{ fontWeight: 600, fontSize: 11, minWidth: 60 }}
              />
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                color: t.type === "credit" ? "success.main" : "error.main",
              }}
            >
              {t.type === "credit" ? "+" : "-"}
              {INR(t.amount)}
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                label={t.wallet_type === "commission" ? "Commission" : "Main"}
                color={t.wallet_type === "commission" ? "warning" : "primary"}
                sx={{ fontSize: 10 }}
              />
            </TableCell>
            <TableCell
              sx={{
                maxWidth: 250,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t.description || "—"}
            </TableCell>
            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
              {t.reference_type || "—"}
            </TableCell>
            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
              {fmtDateTime(t.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PayinsTab({ payins }) {
  if (!payins?.length) return <EmptyState label="No payins" />;
  return (
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>Amount</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Net Credit</TableCell>
          <TableCell>Commission</TableCell>
          <TableCell>Gateway TXN</TableCell>
          <TableCell>Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {payins.map((p) => (
          <TableRow key={p.id} hover>
            <TableCell sx={{ fontWeight: 600 }}>{INR(p.amount)}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={p.status}
                color={statusColor(p.status)}
                sx={{ fontWeight: 600, fontSize: 11 }}
              />
            </TableCell>
            <TableCell>
              {p.net_amount != null ? INR(p.net_amount) : "—"}
            </TableCell>
            <TableCell>
              {p.total_commission != null ? INR(p.total_commission) : "—"}
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontFamily: "monospace",
                color: "text.secondary",
              }}
            >
              {p.gateway_txn_id || "—"}
            </TableCell>
            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
              {fmtDateTime(p.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PayoutsTab({ payouts }) {
  if (!payouts?.length) return <EmptyState label="No payouts" />;
  return (
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>Amount</TableCell>
          <TableCell>Wallet</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Bank</TableCell>
          <TableCell>Gateway TXN</TableCell>
          <TableCell>Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {payouts.map((p) => (
          <TableRow key={p.id} hover>
            <TableCell sx={{ fontWeight: 600 }}>{INR(p.amount)}</TableCell>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                label={p.wallet_type === "main" ? "Main" : "Commission"}
                color={p.wallet_type === "main" ? "primary" : "warning"}
                sx={{ fontSize: 10 }}
              />
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                label={p.status}
                color={payoutStatusColor(p.status)}
                sx={{ fontWeight: 600, fontSize: 11 }}
              />
            </TableCell>
            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
              {p.bank_account_number
                ? `••••${p.bank_account_number.slice(-4)} (${p.ifsc_code || ""})`
                : "—"}
            </TableCell>
            <TableCell
              sx={{
                fontSize: 11,
                fontFamily: "monospace",
                color: "text.secondary",
              }}
            >
              {p.gateway_txn_id || "—"}
            </TableCell>
            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
              {fmtDateTime(p.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Full commission ledger for the user, fetched paginated straight from the
 * ledger (not the capped "recent 20" on the detail payload) so admin can
 * verify every earned row. Shows the source of each row so hand-inserted / test
 * entries with no backing payin are obvious.
 */
function CommissionsTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    adminGetUserCommissions(userId, page, limit)
      .then(({ data }) => {
        if (!alive) return;
        setRows(data.earnings || []);
        setTotal(data.pagination?.total || 0);
        setTotalEarned(data.totalEarned || 0);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [userId, page]);

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!rows.length) return <EmptyState label="No commissions" />;

  return (
    <Stack gap={1.5}>
      <Typography variant="body2" color="text.secondary">
        {total} commission {total === 1 ? "entry" : "entries"} · lifetime earned{" "}
        {INR(totalEarned)}
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Payin Amount</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Commission</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>
                  {c.from_first_name
                    ? `${c.from_first_name} ${c.from_last_name || ""}`
                    : "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={c.source === "manual" ? "warning" : "default"}
                    label={`${COMMISSION_SOURCE_LABEL[c.source] || c.source}${
                      c.gateway ? ` · ${c.gateway}` : ""
                    }`}
                    sx={{ fontSize: 11 }}
                  />
                </TableCell>
                <TableCell>{INR(c.payin_amount)}</TableCell>
                <TableCell>{Number(c.percentage || 0)}%</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "success.main" }}>
                  +{INR(c.commission_amount)}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                  {fmtDateTime(c.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {Math.ceil(total / limit) > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Stack>
  );
}

function DownlineTab({ users, onViewUser }) {
  if (!users?.length) return <EmptyState label="No downline users" />;
  return (
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>User</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Main Balance</TableCell>
          <TableCell>Commission Balance</TableCell>
          <TableCell>Joined</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((u) => (
          <TableRow
            key={u.id}
            hover
            sx={{ cursor: "pointer" }}
            onClick={() => onViewUser(u.id)}
          >
            <TableCell>
              <Stack direction="row" alignItems="center" gap={1}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: ROLE_COLORS[u.role_name] || "#1c6b8c",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {(u.first_name || "?")[0]}
                  {(u.last_name || "?")[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                    {u.first_name} {u.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email || u.mobile_number}
                  </Typography>
                </Box>
              </Stack>
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                label={(u.role_name || "").replace(/_/g, " ")}
                sx={{
                  bgcolor: `${ROLE_COLORS[u.role_name] || "#1c6b8c"}15`,
                  color: ROLE_COLORS[u.role_name] || "#1c6b8c",
                  fontWeight: 600,
                  fontSize: 10,
                }}
              />
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                label={u.is_active ? "Active" : "Inactive"}
                color={u.is_active ? "success" : "default"}
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 500 }}>
              {INR(u.main_balance)}
            </TableCell>
            <TableCell sx={{ fontWeight: 500 }}>
              {INR(u.commission_balance)}
            </TableCell>
            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
              {fmtDate(u.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AuditTab({ logs }) {
  if (!logs?.length) return <EmptyState label="No audit logs" />;
  return (
    <Table size="small" sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>Action</TableCell>
          <TableCell>Entity</TableCell>
          <TableCell>Details</TableCell>
          <TableCell>Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {logs.map((l) => (
          <TableRow key={l.id} hover>
            <TableCell>
              <Chip
                size="small"
                label={l.action}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: 10 }}
              />
            </TableCell>
            <TableCell sx={{ fontSize: 12 }}>
              {l.entity_type} {l.entity_id ? `#${l.entity_id.slice(0, 8)}` : ""}
            </TableCell>
            <TableCell
              sx={{
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 12,
              }}
            >
              {l.meta
                ? typeof l.meta === "string"
                  ? l.meta
                  : JSON.stringify(l.meta)
                : "—"}
            </TableCell>
            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
              {fmtDateTime(l.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function EmptyState({ label }) {
  return (
    <Box p={5} textAlign="center">
      <Typography color="text.secondary">{label}</Typography>
    </Box>
  );
}

const statusColor = (s) => {
  if (s === "success") return "success";
  if (s === "failed" || s === "expired") return "error";
  if (s === "initiated") return "info";
  return "warning";
};

const payoutStatusColor = (s) => {
  if (s === "completed") return "success";
  if (s === "rejected" || s === "failed") return "error";
  if (s === "processing" || s === "approved") return "info";
  return "warning";
};

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  const [lockDialog, setLockDialog] = useState(false);
  const [lockReason, setLockReason] = useState("");
  const [lockSubmitting, setLockSubmitting] = useState(false);
  const [lockFeedback, setLockFeedback] = useState(null);

  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustType, setAdjustType] = useState("credit");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustWallet, setAdjustWallet] = useState("main");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);
  const [adjustError, setAdjustError] = useState(null);

  /* Edit details + block login + KYC status */
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  /* POS machines this user may claim collections against */
  const [posDialog, setPosDialog] = useState(false);
  const [posMachines, setPosMachines] = useState([]);
  const [posSelected, setPosSelected] = useState([]);
  const [posLoading, setPosLoading] = useState(false);
  const [posSaving, setPosSaving] = useState(false);

  /* Move to a different upline */
  const [moveDialog, setMoveDialog] = useState(false);
  const [movePreview, setMovePreview] = useState(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveReason, setMoveReason] = useState("");
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const [moveError, setMoveError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminGetUserDetail(id)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.error || "Failed to load user"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLock = async () => {
    if (!lockReason.trim()) return;
    setLockSubmitting(true);
    setLockFeedback(null);
    try {
      const { data: res } = await lockUser(id, lockReason.trim());
      setLockDialog(false);
      setLockReason("");
      setLockFeedback({
        severity: "success",
        msg: `Wallet locked${res?.locked?.length ? ` (${res.locked.length} account(s) affected)` : ""}.`,
      });
      load();
    } catch (e) {
      setLockFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to lock wallet",
      });
    }
    setLockSubmitting(false);
  };

  const handleUnlock = async () => {
    setLockSubmitting(true);
    setLockFeedback(null);
    try {
      await unlockUser(id);
      setLockFeedback({ severity: "success", msg: "Wallet unlocked." });
      load();
    } catch (e) {
      setLockFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to unlock wallet",
      });
    }
    setLockSubmitting(false);
  };

  const openPosDialog = async () => {
    setPosDialog(true);
    setPosLoading(true);
    try {
      const [{ data: all }, { data: mine }] = await Promise.all([
        adminListPosMachines(),
        adminGetUserPosMachines(id),
      ]);
      setPosMachines((all.machines || []).filter((m) => m.is_active));
      setPosSelected(mine.machineIds || []);
    } catch (e) {
      setActionFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not load POS machines",
      });
      setPosDialog(false);
    } finally {
      setPosLoading(false);
    }
  };

  const handleSavePosMachines = async () => {
    setPosSaving(true);
    try {
      await adminSetUserPosMachines(id, posSelected);
      setPosDialog(false);
      setActionFeedback({
        severity: "success",
        msg: posSelected.length
          ? `${posSelected.length} POS machine(s) assigned.`
          : "All POS machines unassigned — this user will see every active machine.",
      });
    } catch (e) {
      setActionFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not save",
      });
    } finally {
      setPosSaving(false);
    }
  };

  const openMoveDialog = async () => {
    setMoveDialog(true);
    setMoveTarget(null);
    setMoveReason("");
    setMoveError(null);
    setMoveLoading(true);
    try {
      const { data: preview } = await adminGetMovePreview(id);
      setMovePreview(preview);
    } catch (e) {
      setMoveError(e.response?.data?.error || "Could not load upline options");
    } finally {
      setMoveLoading(false);
    }
  };

  const handleMove = async () => {
    if (!moveTarget) return;
    setMoveSubmitting(true);
    setMoveError(null);
    try {
      const { data: res } = await adminMoveUser(
        id,
        moveTarget.id,
        moveReason.trim(),
      );
      setMoveDialog(false);
      setActionFeedback({
        severity: "success",
        msg: `Moved to the new upline${
          res?.movedWithUser
            ? ` — ${res.movedWithUser} downline account(s) moved with them.`
            : "."
        }`,
      });
      load();
    } catch (e) {
      setMoveError(e.response?.data?.error || "Could not move the user");
    } finally {
      setMoveSubmitting(false);
    }
  };

  const openEditDialog = () => {
    const u = data?.user || {};
    setEditForm({
      firstName: u.first_name || "",
      lastName: u.last_name || "",
      email: u.email || "",
      mobileNumber: u.mobile_number || "",
      gender: u.gender || "",
      city: u.city || "",
      state: u.state || "",
      pincode: u.pincode || "",
      panCardNumber: u.pan_card_number || "",
      aadharCardNumber: u.aadhar_card_number || "",
      nameOnAadhar: u.name_on_aadhar || "",
      bankAccountNumber: u.bank_account_number || "",
      ifscCode: u.ifsc_code || "",
    });
    setActionFeedback(null);
    setEditDialog(true);
  };

  const setEdit = (key) => (e) =>
    setEditForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSaveEdit = async () => {
    setEditSubmitting(true);
    setActionFeedback(null);
    try {
      await adminUpdateUser(id, editForm);
      setEditDialog(false);
      setActionFeedback({ severity: "success", msg: "User details updated." });
      load();
    } catch (e) {
      setActionFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to update user",
      });
    }
    setEditSubmitting(false);
  };

  const handleToggleActive = async () => {
    const u = data?.user || {};
    const next = !u.is_active;
    if (
      !next &&
      !window.confirm("Block this user from logging in?")
    )
      return;
    setActionSubmitting(true);
    setActionFeedback(null);
    try {
      await adminSetUserActive(id, next);
      setActionFeedback({
        severity: "success",
        msg: next ? "User can log in again." : "User is blocked from login.",
      });
      load();
    } catch (e) {
      setActionFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to change login access",
      });
    }
    setActionSubmitting(false);
  };

  const handleKycUpload = async (docType, dataUrl) => {
    setActionFeedback(null);
    try {
      await adminUploadKyc(id, docType, dataUrl);
      setActionFeedback({
        severity: "success",
        msg: `${docType.toUpperCase()} document updated.`,
      });
      load();
    } catch (e) {
      setActionFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to upload document",
      });
    }
  };

  const handleKycStatus = async (status) => {
    let reason;
    if (status === "rejected") {
      reason = window.prompt("Reason for rejecting KYC (shown to the user):");
      if (reason === null) return;
    }
    setActionSubmitting(true);
    setActionFeedback(null);
    try {
      await adminSetApproval(id, status, reason);
      setActionFeedback({ severity: "success", msg: `KYC marked ${status}.` });
      load();
    } catch (e) {
      setActionFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to update KYC status",
      });
    }
    setActionSubmitting(false);
  };

  const openAdjustDialog = () => {
    setAdjustType("credit");
    setAdjustAmount("");
    setAdjustWallet("main");
    setAdjustReason("");
    setAdjustError(null);
    setAdjustDialog(true);
  };

  const handleAdjust = async () => {
    const amount = Number(adjustAmount);
    if (!(amount > 0) || !adjustReason.trim()) return;
    setAdjustSubmitting(true);
    setAdjustError(null);
    try {
      const { data: res } = await adjustUserWallet(id, {
        type: adjustType,
        amount,
        reason: adjustReason.trim(),
        walletType: adjustWallet,
      });
      setAdjustDialog(false);
      setLockFeedback({
        severity: "success",
        msg: `Wallet ${res.type}ed by ${INR(res.amount)}. New balance ${INR(
          res.balanceAfter,
        )}.`,
      });
      load();
    } catch (e) {
      setAdjustError(e.response?.data?.error || "Failed to adjust wallet");
    }
    setAdjustSubmitting(false);
  };

  if (loading) {
    return (
      <Box p={5} textAlign="center">
        <CircularProgress />
        <Typography mt={2} color="text.secondary">
          Loading user details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={5} textAlign="center">
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!data) return null;

  const {
    user,
    wallets,
    metrics,
    charts,
    recentTransactions,
    recentPayins,
    recentPayouts,
    recentCommissions,
    downlineUsers,
    auditLogs,
    kycDocuments,
  } = data;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={3}
        flexWrap="wrap"
      >
        <Tooltip title="Back to users">
          <IconButton onClick={() => navigate("/admin/users")} sx={{ mr: 1 }}>
            <ArrowBackRoundedIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700}>
          User Details
        </Typography>
        <Box flex={1} />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<MenuBookRoundedIcon />}
          onClick={() =>
            navigate(`/admin/users/${id}/passbook`, {
              state: {
                name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
              },
            })
          }
          sx={{ borderRadius: 3 }}
        >
          Passbook
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<EditRoundedIcon />}
          onClick={openEditDialog}
          sx={{ borderRadius: 3 }}
        >
          Edit Details
        </Button>
        <Button
          variant="outlined"
          color={user.is_active ? "error" : "success"}
          startIcon={
            user.is_active ? <BlockRoundedIcon /> : <CheckCircleRoundedIcon />
          }
          onClick={handleToggleActive}
          disabled={actionSubmitting}
          sx={{ borderRadius: 3 }}
        >
          {user.is_active ? "Block Login" : "Unblock Login"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<TuneRoundedIcon />}
          onClick={openAdjustDialog}
          sx={{ borderRadius: 3 }}
        >
          Adjust Wallet
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<SwapHorizRoundedIcon />}
          onClick={openMoveDialog}
          sx={{ borderRadius: 3 }}
        >
          Move Upline
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PointOfSaleRoundedIcon />}
          onClick={openPosDialog}
          sx={{ borderRadius: 3 }}
        >
          POS Machines
        </Button>
        {user.is_locked ? (
          <Button
            variant="outlined"
            color="success"
            startIcon={<LockOpenRoundedIcon />}
            onClick={handleUnlock}
            disabled={lockSubmitting}
            sx={{ borderRadius: 3 }}
          >
            Unlock Wallet
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="error"
            startIcon={<LockRoundedIcon />}
            onClick={() => {
              setLockReason("");
              setLockFeedback(null);
              setLockDialog(true);
            }}
            sx={{ borderRadius: 3 }}
          >
            Lock Wallet
          </Button>
        )}
      </Stack>

      {lockFeedback && (
        <Alert
          severity={lockFeedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setLockFeedback(null)}
        >
          {lockFeedback.msg}
        </Alert>
      )}

      {actionFeedback && (
        <Alert
          severity={actionFeedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setActionFeedback(null)}
        >
          {actionFeedback.msg}
        </Alert>
      )}

      {/* Profile card */}
      <ProfileSection user={user} />

      {/* KYC approval controls */}
      <Stack
        direction="row"
        gap={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{ mt: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          KYC status:{" "}
          <b style={{ textTransform: "capitalize" }}>
            {user.approval_status || "approved"}
          </b>
        </Typography>
        <Box flex={1} />
        <Button
          size="small"
          variant="outlined"
          color="success"
          disabled={actionSubmitting || user.approval_status === "approved"}
          onClick={() => handleKycStatus("approved")}
        >
          Approve KYC
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={actionSubmitting || user.approval_status === "rejected"}
          onClick={() => handleKycStatus("rejected")}
        >
          Reject KYC
        </Button>
      </Stack>

      {/* KYC documents */}
      <KycDocumentsSection
        user={user}
        kycDocuments={kycDocuments}
        onUpload={handleKycUpload}
      />

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AccountBalanceWalletRoundedIcon />}
            label="Main Balance"
            value={INR(wallets.main.balance)}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MonetizationOnRoundedIcon />}
            label="Commission Balance"
            value={INR(wallets.commission.balance)}
            sub={`Lifetime earned: ${INR(metrics.commissions.totalEarned)}`}
            color="#1c6b8c"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUpRoundedIcon />}
            label="Total Payins"
            value={INR(metrics.payins.total)}
            sub={`${metrics.payins.count} successful`}
            color="#0ea5e9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingDownRoundedIcon />}
            label="Total Payouts"
            value={INR(metrics.payouts.completedAmount)}
            sub={`${metrics.payouts.completedCount} completed, ${metrics.payouts.pendingCount} pending`}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ReceiptRoundedIcon />}
            label="Commission Earnings"
            value={INR(metrics.commissions.totalEarned)}
            sub={`From ${metrics.commissions.count} payins`}
            color="#2f8fb5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleRoundedIcon />}
            label="Direct Downline"
            value={metrics.downlineCount}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingDownRoundedIcon />}
            label="Pending Payouts"
            value={INR(metrics.payouts.pendingAmount)}
            sub={`${metrics.payouts.pendingCount} awaiting approval`}
            color="#f97316"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingDownRoundedIcon />}
            label="Failed Payouts"
            value={metrics.payouts.failedCount}
            color="#dc2626"
          />
        </Grid>
      </Grid>

      {/* Per-user payin gateway control */}
      <Box sx={{ mt: 3 }}>
        <UserPayinGatewaysCard userId={id} />
      </Box>

      {/* Monthly chart */}
      <Card
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={2}>
          Monthly Activity (Last 6 Months)
        </Typography>
        <MonthlyChart
          monthlyPayins={charts.monthlyPayins || []}
          monthlyPayouts={charts.monthlyPayouts || []}
          monthlyCommissions={charts.monthlyCommissions || []}
        />
      </Card>

      {/* Tabbed history */}
      <Card
        sx={{
          mt: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              minHeight: 48,
            },
          }}
        >
          <Tab label={`Transactions (${recentTransactions?.length || 0})`} />
          <Tab label={`Payins (${recentPayins?.length || 0})`} />
          <Tab label={`Payouts (${recentPayouts?.length || 0})`} />
          <Tab label="Commissions" />
          <Tab label={`Downline (${downlineUsers?.length || 0})`} />
          <Tab label={`Audit Log (${auditLogs?.length || 0})`} />
        </Tabs>
        <Box sx={{ overflow: "auto" }}>
          {tab === 0 && <TransactionsTab transactions={recentTransactions} />}
          {tab === 1 && <PayinsTab payins={recentPayins} />}
          {tab === 2 && <PayoutsTab payouts={recentPayouts} />}
          {tab === 3 && <CommissionsTab userId={id} />}
          {tab === 4 && (
            <DownlineTab
              users={downlineUsers}
              onViewUser={(uid) => navigate(`/admin/users/${uid}`)}
            />
          )}
          {tab === 5 && <AuditTab logs={auditLogs} />}
        </Box>
      </Card>

      {/* Lock wallet dialog */}
      <Dialog
        open={lockDialog}
        onClose={() => setLockDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Lock Wallet</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Locking freezes payouts and bill payments (payins still allowed)
              and also locks the user's upline Distributor &amp; Master
              Distributor.
            </Alert>
            <TextField
              fullWidth
              label="Reason"
              multiline
              minRows={2}
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              placeholder="e.g. Chargeback under investigation"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLockDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleLock}
            disabled={lockSubmitting || !lockReason.trim()}
          >
            {lockSubmitting ? "Locking…" : "Lock Wallet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* POS machines this user may claim against */}
      <Dialog
        open={posDialog}
        onClose={() => !posSaving && setPosDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>POS Machines</DialogTitle>
        <DialogContent>
          {posLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : posMachines.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No active POS machines are registered. Add them under{" "}
              <b>QR &amp; POS Setup</b> first.
            </Alert>
          ) : (
            <Stack sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Tick the machines this user pays on. Their collections are
                labelled with the machine they picked. Leave all unticked and
                they'll see every active machine instead.
              </Typography>
              {posMachines.map((m) => (
                <FormControlLabel
                  key={m.id}
                  control={
                    <Checkbox
                      checked={posSelected.includes(m.id)}
                      onChange={(e) =>
                        setPosSelected((prev) =>
                          e.target.checked
                            ? [...prev, m.id]
                            : prev.filter((x) => x !== m.id),
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {m.label}
                      </Typography>
                      {m.terminal_id && (
                        <Typography variant="caption" color="text.secondary">
                          TID {m.terminal_id}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPosDialog(false)} disabled={posSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePosMachines}
            disabled={posSaving || posLoading}
          >
            {posSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to a different upline */}
      <Dialog
        open={moveDialog}
        onClose={() => setMoveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Move to a Different Upline</DialogTitle>
        <DialogContent>
          {moveLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {moveError && <Alert severity="error">{moveError}</Alert>}

              <Alert severity="info">
                The user's entire downline moves with them. Commission already
                earned stays on the record as-is — only future commission
                follows the new chain.
              </Alert>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Currently under
                </Typography>
                <Typography fontWeight={600}>
                  {movePreview?.currentParent
                    ? `${movePreview.currentParent.first_name || ""} ${
                        movePreview.currentParent.last_name || ""
                      }`.trim() +
                      ` — ${(movePreview.currentParent.role_name || "").replace(/_/g, " ")}`
                    : "No upline (top of tree)"}
                </Typography>
              </Box>

              {movePreview?.movesWithUser > 0 && (
                <Alert severity="warning">
                  <b>{movePreview.movesWithUser}</b> downline account
                  {movePreview.movesWithUser === 1 ? "" : "s"} will move with
                  this user.
                </Alert>
              )}

              <Autocomplete
                options={movePreview?.eligibleParents || []}
                value={moveTarget}
                onChange={(_, v) => setMoveTarget(v)}
                getOptionLabel={(o) =>
                  `${`${o.first_name || ""} ${o.last_name || ""}`.trim()} — ${(
                    o.role_name || ""
                  ).replace(/_/g, " ")} (${o.mobile_number || o.email || ""})`
                }
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText="No eligible upline — the new upline must be a higher level than this user"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="New upline"
                    placeholder="Search by name, role or number"
                  />
                )}
              />

              <TextField
                fullWidth
                label="Reason"
                multiline
                minRows={2}
                value={moveReason}
                onChange={(e) => setMoveReason(e.target.value)}
                placeholder="e.g. Distributor territory reassignment"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMoveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleMove}
            disabled={moveSubmitting || !moveTarget || !moveReason.trim()}
          >
            {moveSubmitting ? "Moving…" : "Move User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust wallet dialog */}
      <Dialog
        open={adjustDialog}
        onClose={() => setAdjustDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Adjust Wallet</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Manually credit or debit this user's wallet. A reason is required
              and the adjustment is recorded as a transaction.
            </Alert>
            {adjustError && <Alert severity="error">{adjustError}</Alert>}
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={adjustType}
              onChange={(_, v) => v && setAdjustType(v)}
              size="small"
            >
              <ToggleButton
                value="credit"
                color="success"
                sx={{ fontWeight: 600, textTransform: "none" }}
              >
                Credit (add)
              </ToggleButton>
              <ToggleButton
                value="debit"
                color="error"
                sx={{ fontWeight: 600, textTransform: "none" }}
              >
                Debit (deduct)
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              fullWidth
              select
              label="Wallet"
              value={adjustWallet}
              onChange={(e) => setAdjustWallet(e.target.value)}
            >
              <MenuItem value="main">
                Main — {INR(wallets.main.balance)}
              </MenuItem>
              <MenuItem value="commission">
                Commission — {INR(wallets.commission.balance)}
              </MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Amount"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Reason"
              multiline
              minRows={2}
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="e.g. Reversing wrongly-refunded BBPS bill"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdjustDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={adjustType === "credit" ? "success" : "error"}
            onClick={handleAdjust}
            disabled={
              adjustSubmitting ||
              !(Number(adjustAmount) > 0) ||
              !adjustReason.trim()
            }
          >
            {adjustSubmitting
              ? "Saving…"
              : adjustType === "credit"
                ? "Credit Wallet"
                : "Debit Wallet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit user details */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Edit User Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[
              ["firstName", "First name"],
              ["lastName", "Last name"],
              ["email", "Email"],
              ["mobileNumber", "Mobile number"],
              ["gender", "Gender"],
              ["city", "City"],
              ["state", "State"],
              ["pincode", "Pincode"],
              ["panCardNumber", "PAN"],
              ["aadharCardNumber", "Aadhaar"],
              ["nameOnAadhar", "Name on Aadhaar"],
              ["bankAccountNumber", "Bank account number"],
              ["ifscCode", "IFSC"],
            ].map(([key, label]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  value={editForm[key] ?? ""}
                  onChange={setEdit(key)}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveEdit}
            disabled={editSubmitting}
          >
            {editSubmitting ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
